import { useQuery } from "@apollo/client";
import {
  InvoiceByIdDocument,
  InvoiceDetailFragmentFragmentDoc,
  InvoicePaymentStatusEnum,
  InvoiceStatusEnum,
  VoucherDiscountTypeEnum,
} from "@/__generated__/graphql";
import { getFragmentData } from "@/__generated__";
import type { LineItemDetail } from "@/types/line-item";
import type { InvoiceDetail } from "@/types/invoice";
import { getInvoiceLineItems } from "@/lib/line-items/helpers";

export type InvoiceDetailData = Omit<
  InvoiceDetail,
  "dueDate" | "issueDate" | "fromDate" | "toDate" | "lineItems"
> & {
  balance: number;
  dueDate: Date;
  issueDate: Date;
  fromDate: Date;
  toDate: Date;
  lineItems: LineItemDetail[];
  qrCodeUrl: string;
  discountAmount: number;
};

export function getInvoiceDetail(
  invoice: InvoiceDetail | null,
): InvoiceDetailData {
  let discountValue = 0;

  if (invoice?.appliedVoucher) {
    const { discountType, discountValue: value } = invoice.appliedVoucher;
    discountValue =
      discountType === VoucherDiscountTypeEnum.Percentage
        ? (invoice.lineItemsTotal * value) / 100
        : value;
  }

  return {
    id: invoice?.id ?? "",
    amount: invoice?.amount ?? 0,
    balance: invoice ? invoice.totalAmount - invoice.paidAmount : 0,
    paidAmount: invoice?.paidAmount ?? 0,
    baseAmount: invoice?.baseAmount ?? 0,
    paymentStatus: invoice?.paymentStatus ?? InvoicePaymentStatusEnum.Unpaid,
    fromDate: invoice ? new Date(invoice.fromDate) : new Date(),
    toDate: invoice ? new Date(invoice.toDate ?? 0) : new Date(),
    issueDate: invoice ? new Date(invoice.issueDate ?? 0) : new Date(),
    dueDate: invoice ? new Date(invoice.dueDate ?? 0) : new Date(),
    status: invoice?.status ?? InvoiceStatusEnum.New,
    pdfUrl: invoice?.pdfUrl ?? "",
    totalAmount: invoice?.totalAmount ?? 0,
    pdfReceiptUrl: invoice?.pdfReceiptUrl ?? "",
    appliedVoucher: invoice?.appliedVoucher ?? null,
    lineItems: invoice?.lineItems?.map(getInvoiceLineItems) ?? [],
    qrCodeUrl: invoice?.qrCodeUrl ?? "",
    lineItemsTotal: invoice?.lineItemsTotal ?? 0,
    discountAmount: discountValue,
    departments: invoice?.departments,
  };
}

export const useInvoiceByIdQuery = (invoiceId: string) => {
  const result = useQuery(InvoiceByIdDocument, {
    variables: {
      id: invoiceId,
    },
  });

  const invoiceFragment = getFragmentData(
    InvoiceDetailFragmentFragmentDoc,
    result.data?.invoiceById ?? null,
  );

  const invoice = getInvoiceDetail(invoiceFragment);

  return { ...result, invoice };
};
